<template>
  <div class="result-page-wrapper">
    
    <!-- 1. خلفية وديكور -->
    <div class="background-shapes"></div>
    
    <div class="container py-5 position-relative z-index-1">
      
      <!-- الهيدر والبحث (يختفي عند الطباعة) -->
      <div class="row justify-content-center mb-4 d-print-none">
        <div class="col-lg-8 text-center" data-aos="fade-down">
          <h1 class="text-white fw-bold title-shadow mb-2">بوابة نتائج التعليم الأساسي</h1>
          <h4 class="text-gold mb-4">محافظة شمال سيناء - الدور الأول 2026</h4>

          <!-- صندوق البحث -->
          <div class="search-card p-4 bg-white rounded-4 shadow-lg mb-4">
            <form @submit.prevent="getResult">
              <div class="input-group input-group-lg border rounded-pill overflow-hidden">
                <span class="input-group-text bg-white border-0 ps-4"><i class="fas fa-search text-primary"></i></span>
                <input 
                  type="number" 
                  v-model="seatNumber" 
                  class="form-control border-0 fw-bold fs-4 text-center text-primary" 
                  placeholder="أدخل رقم الجلوس" 
                  required
                >
                <button class="btn btn-primary px-5 fw-bold" type="submit" :disabled="loading">
                  <span v-if="loading" class="spinner-border spinner-border-sm"></span>
                  <span v-else>بحث</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <!-- أزرار التبديل (تظهر فقط عند وجود نتيجة) -->
      <div v-if="result" class="row justify-content-center mb-4 d-print-none" data-aos="fade-up">
        <div class="col-auto">
            <div class="btn-group shadow-lg rounded-pill overflow-hidden">
                <button @click="viewMode = 'card'" class="btn px-4 fw-bold" :class="viewMode === 'card' ? 'btn-gold text-dark' : 'btn-light text-muted'">
                    <i class="fas fa-certificate me-2"></i> شهادة تقدير (ملون)
                </button>
                <button @click="viewMode = 'table'" class="btn px-4 fw-bold" :class="viewMode === 'table' ? 'btn-gold text-dark' : 'btn-light text-muted'">
                    <i class="fas fa-file-alt me-2"></i> بيان درجات (رسمي)
                </button>
            </div>
        </div>
      </div>

      <!-- ============================================= -->
      <!-- Mode 1: العرض الحديث (Card View) -->
      <!-- ============================================= -->
      <div v-if="result && viewMode === 'card'" class="row justify-content-center" id="printAreaCard">
        <div class="col-lg-10">
          <div class="certificate-card bg-white rounded-4 shadow-lg overflow-hidden position-relative animate__animated animate__fadeIn">
            <!-- شريط الحالة -->
            <div :class="getStatusClass(result.status)" class="status-ribbon text-center py-2 text-white fw-bold">
              {{ result.status_text }}
            </div>

            <div class="card-body p-5">
              <!-- رأس الشهادة -->
              <div class="row align-items-center mb-4 border-bottom pb-4">
                <div class="col-md-8">
                  <h2 class="fw-bolder text-primary mb-1">{{ result.student.name }}</h2>
                  <p class="text-muted fs-5 mb-1"><i class="fas fa-school me-2"></i> {{ result.student.school }}</p>
                  <span class="badge bg-dark fs-6">رقم الجلوس: {{ result.student.seat_number }}</span>
                </div>
                <div class="col-md-4 text-center mt-3 mt-md-0">
                  <!-- الدائرة الملونة -->
                  <div class="total-score-circle mx-auto" :class="getScoreColor(result.percentage)">
                    <div class="score-content">
                      <span class="fs-1 fw-bold">{{ result.total_score }}</span>
                      <span class="fs-6 text-muted">من {{ result.max_total }}</span>
                    </div>
                    <svg viewBox="0 0 36 36" class="circular-chart">
                      <path class="circle-bg" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                      <path class="circle" :stroke-dasharray="result.percentage + ', 100'" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                    </svg>
                  </div>
                  <div class="mt-2 fw-bold text-primary">{{ result.percentage }}%</div>
                </div>
              </div>

              <!-- تفاصيل المواد -->
              <h5 class="fw-bold text-secondary mb-3">تفاصيل الدرجات</h5>
              <div class="row g-3">
                <div class="col-md-6 col-lg-4" v-for="sub in result.subjects" :key="sub.id">
                  <div class="subject-card p-3 rounded border h-100" :class="sub.is_fail ? 'border-danger bg-danger-subtle' : 'border-light bg-light'">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <span class="fw-bold">{{ sub.name }}</span>
                      <span class="badge" :class="sub.is_fail ? 'bg-danger' : 'bg-success'">{{ sub.degree }}</span>
                    </div>
                    <div class="progress" style="height: 6px;">
                      <div class="progress-bar" :class="sub.is_fail ? 'bg-danger' : 'bg-success'" :style="{ width: (sub.degree / sub.max * 100) + '%' }"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- أزرار الإجراءات (طباعة ومشاركة) -->
            <div class="card-footer bg-white border-top text-center py-3 d-print-none">
               <div class="d-flex justify-content-center gap-3">
                   <button @click="printResult" class="btn btn-outline-dark fw-bold px-4">
                     <i class="fas fa-print me-2"></i> طباعة الشهادة
                   </button>
                   
                   <button @click="shareResult" class="btn btn-success fw-bold px-4 shadow-sm">
                     <i class="fas fa-share-nodes me-2"></i> مشاركة النتيجة
                   </button>
               </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ============================================= -->
      <!-- Mode 2: البيان الرسمي (Official Statement) -->
      <!-- ============================================= -->
      <div v-if="result && viewMode === 'table'" class="row justify-content-center" id="printAreaTable">
        <div class="col-lg-10">
          <div class="official-sheet bg-white p-4 mx-auto position-relative overflow-hidden animate__animated animate__fadeIn">
            
            <div class="watermark-logo"></div>

            <!-- الترويسة -->
            <div class="row border-bottom border-3 border-dark pb-3 mb-3 align-items-center text-center header-row">
                <div class="col-3 text-end">
                    <h6 class="fw-bold mb-1">محافظة شمال سيناء</h6>
                    <h6 class="fw-bold mb-1">مديرية التربية والتعليم</h6>
                    <h6 class="fw-bold mb-0 small">لجنة النظام والمراقبة</h6>
                </div>
                <div class="col-6">
                    <h4 class="fw-bolder text-decoration-underline">شهادة إتمام مرحلة التعليم الأساسي</h4>
                    <h6 class="fw-bold mt-1">الدور الأول - العام الدراسي 2025 / 2026</h6>
                </div>
                <div class="col-3 text-start">
                    <div class="seat-box border border-2 border-dark p-2 d-inline-block text-center" style="min-width: 120px;">
                        <span class="d-block small fw-bold text-muted">رقم الجلوس</span>
                        <span class="d-block fs-3 fw-bold font-monospace">{{ result.student.seat_number }}</span>
                    </div>
                </div>
            </div>

            <!-- بيانات الطالب -->
            <div class="student-info-box mb-4">
                <div class="row g-0 border border-dark border-2">
                    <div class="col-2 bg-light border-end border-dark d-flex align-items-center justify-content-center fw-bold">اسم الطالب</div>
                    <div class="col-6 border-end border-dark p-2 fw-bold fs-5 text-primary-dark">{{ result.student.name }}</div>
                    <div class="col-2 bg-light border-end border-dark d-flex align-items-center justify-content-center fw-bold">المدرسة</div>
                    <div class="col-2 p-2 small fw-bold text-center d-flex align-items-center justify-content-center">{{ result.student.school }}</div>
                </div>
            </div>

            <!-- جدول المواد -->
            <h6 class="fw-bold text-decoration-underline mb-2">أولاً: المواد الأساسية</h6>
            <table class="table table-bordered border-dark text-center align-middle grades-table mb-4">
                <thead class="bg-light">
                    <tr>
                        <th width="40%">المادة</th>
                        <th width="20%">العظمى</th>
                        <th width="20%">الصغرى</th>
                        <th width="20%">الدرجة</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="sub in basicSubjects" :key="sub.id">
                        <td class="text-start pe-4 fw-bold">{{ sub.name }}</td>
                        <td>{{ sub.max }}</td>
                        <td>{{ sub.max / 2 }}</td>
                        <td class="fw-bold fs-5" :class="sub.is_fail ? 'text-danger text-decoration-underline' : ''">
                            {{ sub.degree }}
                        </td>
                    </tr>
                </tbody>
                <tfoot class="total-row">
                    <tr>
                        <td class="text-start pe-4">المجموع الكلي</td>
                        <td>{{ result.max_total }}</td>
                        <td>{{ result.max_total / 2 }}</td>
                        <td class="fs-4" :class="result.status === 'pass' ? 'text-success' : 'text-danger'">
                            {{ result.total_score }}
                        </td>
                    </tr>
                </tfoot>
            </table>

            <!-- الحالة النهائية -->
            <div class="status-box mt-4 text-center border-top border-bottom border-dark py-2 mb-4">
                <span class="fw-bold ms-2">النتيجة النهائية:</span>
                <span class="fs-4 fw-bolder" :class="result.status === 'pass' ? 'text-success' : 'text-danger'">
                    {{ result.status_text }}
                </span>
            </div>

            <!-- زر الطباعة والمشاركة -->
            <div class="card-footer bg-white border-0 text-center py-3 d-print-none mt-2">
               <div class="d-flex justify-content-center gap-3">
                   <button @click="printResult" class="btn btn-dark fw-bold px-4">
                     <i class="fas fa-print me-2"></i> طباعة البيان
                   </button>
                   <button @click="shareResult" class="btn btn-outline-success fw-bold px-4">
                     <i class="fas fa-share-nodes me-2"></i> مشاركة
                   </button>
               </div>
            </div>

          </div>
        </div>
      </div>

      <!-- رسالة خطأ -->
      <div v-if="error" class="row justify-content-center mt-4">
        <div class="col-md-6">
          <div class="alert alert-danger shadow text-center border-0 fw-bold">
            <i class="fas fa-exclamation-circle me-2"></i> {{ error }}
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import api from '@/api/axios';
// import confetti from "canvas-confetti"; // يمكن تفعيله إذا أردت الاحتفال

export default {
  name: "ResultPortal",
  data() {
    return {
      seatNumber: '',
      loading: false,
      result: null,
      error: null,
      viewMode: 'card', 
    };
  },
  computed: {
    basicSubjects() {
        if (!this.result) return [];
        const nonBasicNames = ['التربية الدينية', 'التربية الفنية', 'الحاسب الآلي', 'تكنولوجيا المعلومات', 'تربية رياضية'];
        return this.result.subjects.filter(sub => !nonBasicNames.some(name => sub.name.includes(name)) && !sub.is_non_add);
    },
    nonBasicSubjects() {
        if (!this.result) return [];
        const nonBasicNames = ['التربية الدينية', 'التربية الفنية', 'الحاسب الآلي', 'تكنولوجيا المعلومات', 'تربية رياضية'];
        return this.result.subjects.filter(sub => nonBasicNames.some(name => sub.name.includes(name)) || sub.is_non_add);
    }
  },
  methods: {
    async getResult() {
      if(!this.seatNumber) return;
      this.loading = true;
      this.error = null;
      this.result = null;

      try {
        const res = await api.get(`/public/result/${this.seatNumber}`);
        this.result = res.data;
      } catch (e) {
        if(e.response && e.response.status === 404) {
            this.error = "رقم الجلوس غير صحيح، يرجى التأكد والمحاولة مرة أخرى.";
        } else if (e.response && e.response.status === 403) {
            this.error = e.response.data.message; 
        } else {
            this.error = "حدث خطأ في الاتصال بالسيرفر.";
        }
      } finally {
        this.loading = false;
      }
    },

    getStatusClass(status) {
        if(status === 'pass') return 'bg-success';
        if(status === 'round2') return 'bg-danger';
        return 'bg-warning text-dark';
    },

    getScoreColor(percent) {
        if(percent >= 85) return 'score-excellent';
        if(percent >= 75) return 'score-verygood';
        if(percent >= 65) return 'score-good';
        if(percent >= 50) return 'score-pass';
        return 'score-fail';
    },

    printResult() {
        window.print();
    },

    // --- دالة المشاركة الذكية ---
    async shareResult() {
        if (!this.result) return;

        const title = "نتيجة الشهادة الإعدادية";
        const text = `🎉 ألف مبروك!\nالطالب: ${this.result.student.name}\nالمجموع: ${this.result.total_score} / ${this.result.max_total}\nالنتيجة: ${this.result.status_text}\n\nشاهد النتيجة كاملة:`;
        const url = window.location.href; // يمكن تخصيص الرابط ليكون برقم الجلوس

        // استخدام Web Share API للموبايل
        if (navigator.share) {
            try {
                await navigator.share({
                    title: title,
                    text: text,
                    url: url
                });
            } catch (err) {
                console.log('Share canceled');
            }
        } else {
            // نسخ الرابط للكمبيوتر
            try {
                await navigator.clipboard.writeText(`${text}\n${url}`);
                alert("تم نسخ تفاصيل النتيجة! يمكنك لصقها الآن في واتساب أو فيسبوك.");
            } catch (err) {
                alert("المتصفح لا يدعم المشاركة.");
            }
        }
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;800&display=swap');

/* --- الأساسيات --- */
.result-page-wrapper {
    font-family: 'Cairo', sans-serif;
    min-height: 100vh;
    background: linear-gradient(135deg, #002147 0%, #0d47a1 100%);
    position: relative;
    overflow-x: hidden;
}

.background-shapes {
    position: absolute;
    width: 100%; height: 100%;
    background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    z-index: 0;
}

.text-gold { color: #ffd700; }
.title-shadow { text-shadow: 0 4px 10px rgba(0,0,0,0.3); }

/* --- العرض الحديث (Card) --- */
.certificate-card {
    border: 1px solid rgba(0,0,0,0.1);
    background-image: linear-gradient(to bottom right, #fff, #f8f9fa);
}
.status-ribbon { position: absolute; top: 0; left: 0; right: 0; text-transform: uppercase; letter-spacing: 1px; }
.total-score-circle { position: relative; width: 120px; height: 120px; }
.circular-chart { display: block; margin: 0 auto; max-width: 100%; max-height: 100%; }
.circle-bg { fill: none; stroke: #eee; stroke-width: 2.5; }
.circle { fill: none; stroke-width: 2.5; stroke-linecap: round; animation: progress 1s ease-out forwards; }
.score-excellent .circle { stroke: #198754; }
.score-verygood .circle { stroke: #0d6efd; }
.score-good .circle { stroke: #0dcaf0; }
.score-fail .circle { stroke: #dc3545; }
.score-content { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); display: flex; flex-direction: column; align-items: center; line-height: 1; }
@keyframes progress { 0% { stroke-dasharray: 0 100; } }

/* --- العرض الرسمي (Table) --- */
.official-sheet {
    border: 3px double #000;
    min-height: 297mm;
    background-color: #fff;
    position: relative;
    font-family: 'Times New Roman', 'Cairo', serif; 
}
.watermark-logo {
    position: absolute;
    top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    width: 300px; height: 300px;
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
    opacity: 0.05;
    z-index: 0;
    pointer-events: none;
}
.student-info-box { z-index: 1; position: relative; }
.grades-table th { background-color: #f0f0f0; font-weight: 800; border-width: 2px; }
.grades-table td { font-weight: 600; font-size: 15px; }
.total-row { background-color: #f8f9fa; border-top: 3px double #000; font-weight: 900; }
.btn-gold { background-color: #ffd700; border: none; }

/* --- الطباعة --- */
@media print {
    .result-page-wrapper { background: white !important; }
    .search-card, .btn, .d-print-none, .background-shapes { display: none !important; }
    
    /* الحديث */
    #printAreaCard .certificate-card { 
        box-shadow: none !important; 
        border: 2px solid #000 !important; 
        width: 100%; position: absolute; left: 0; top: 0;
    }
    
    /* الرسمي */
    #printAreaTable .official-sheet { 
        box-shadow: none !important; 
        border: none;
        width: 100%; position: absolute; left: 0; top: 0;
    }
    
    .badge { border: 1px solid #000; color: black !important; }
    .progress-bar, .bg-dark, .bg-light { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    .text-white { color: black !important; }
    .bg-dark { color: white !important; background-color: black !important; }
    .text-success { color: black !important; font-weight: bold; }
    .text-danger { color: black !important; text-decoration: underline; }
}
</style>